import type {
  ComponentConfig,
  FormCategoryConfig,
  FormConfig,
  FormPageConfig,
  FormRowConfig,
  FormSectionConfig
} from '@st/ui-config'

export type VisibleSection = {
  title?: string
  categoryRows: FormRowConfig[]
  rows: FormRowConfig[]
}

export type ResolvedFormationPage = Omit<FormPageConfig, 'sections'> & {
  sections: VisibleSection[]
  fieldKeys: string[]
}

export type FormationLayout = {
  pages: ResolvedFormationPage[]
  pageNumberByPageId: Record<string, number>
  pageNumberByAnchor: Record<string, number>
}

/**
 * Resolves the structural facts of a Formation document in one traversal.
 * Rendering, navigation, field projection, and anchor placement must consume
 * this same snapshot; independently evaluating visibility can otherwise make
 * page numbers disagree with the content occupying those pages.
 *
 * The returned page numbers are one-based because they are document locations,
 * not array indexes. Anchors remain semantic keys until this layout boundary
 * assigns them to a visible page.
 */
export function resolveFormationLayout({
  config,
  evalExpr
}: {
  config: FormConfig
  evalExpr: (expr: string | undefined) => unknown
}): FormationLayout {
  const pages: ResolvedFormationPage[] = []
  const pageNumberByPageId: Record<string, number> = {}
  const pageNumberByAnchor: Record<string, number> = {}

  for (const page of config.pages) {
    if (page.sections.length == 0 || !evalExpr(page.showWhen ?? undefined)) continue

    const pageNumber = pages.length + 1
    const sections = page.sections
      .map((section) => filterSectionRows(section, evalExpr))
      .filter((section) => section.rows.length > 0 || section.categoryRows.length > 0)
    const fieldKeys = new Set<string>()

    for (const section of sections) {
      for (const row of [...section.categoryRows, ...section.rows]) {
        for (const component of row.components) {
          for (const key of resolveComponentFieldKeys(component)) fieldKeys.add(key)
          if (component.__typename == 'NoteWidgetConfig') {
            pageNumberByAnchor[component.userInputKey] = pageNumber
          }
        }
      }
    }

    let title = page.title
    if (pageNumber == 1) title = config.name
    pages.push({ ...page, title, sections, fieldKeys: [...fieldKeys] })
    pageNumberByPageId[page.id] = pageNumber
  }

  return { pages, pageNumberByPageId, pageNumberByAnchor }
}

function filterSectionRows(
  config: FormSectionConfig,
  evalExpr: (expr: string | undefined) => unknown
): VisibleSection {
  const rows = config.rows ?? []
  const categoryRows = categoryToRows(config.categories ?? [])
  const isVisible = (row: FormRowConfig) => !!evalExpr(row.showWhen ?? undefined)

  return {
    title: config.title ?? undefined,
    categoryRows: categoryRows.filter(isVisible),
    rows: rows.filter(isVisible)
  }
}

function categoryToRows(categories: FormCategoryConfig[]): FormRowConfig[] {
  const rows: FormRowConfig[] = []

  for (const category of categories) {
    if (category.headerRowComponents.length > 0) {
      rows.push({ id: category.name, components: category.headerRowComponents })
    }
    if (category.rows) rows.push(...category.rows)
  }

  return rows
}

function resolveComponentFieldKeys(config: ComponentConfig): string[] {
  switch (config.__typename) {
    case 'TextInputConfig':
    case 'MoneyInputConfig':
    case 'DateInputConfig':
    case 'YesNoConfig':
    case 'FormSelectCategoryConfig':
    case 'CheckboxConfig':
    case 'RadioSelectConfig':
    case 'MultiSelectConfig':
    case 'DropdownConfig':
    case 'NoteWidgetConfig':
      return [config.userInputKey]
    case 'NameInputConfig':
      return [`${config.userInputKey}.first_name`, `${config.userInputKey}.last_name`]
    case 'AddressInputConfig':
      return ['street', 'city', 'state', 'country', 'zip'].map(
        (suffix) => `${config.userInputKey}.${suffix}`
      )
    case 'PartnerInputConfig':
      return [
        `${config.userInputKey}.type`,
        `${config.userInputKey}.name.first_name`,
        `${config.userInputKey}.name.last_name`,
        `${config.userInputKey}.entity_name`,
        `${config.userInputKey}.tin`
      ]
    case 'StandardTableConfig':
      return config.columns.flatMap((column) =>
        column.components.flatMap(resolveComponentFieldKeys)
      )
    case 'GridTableConfig':
      return Array.from({ length: config.rowCount }, (_, rowIndex) =>
        config.columns.map((column) => `${config.userInputKey}.${rowIndex}.${column.key}`)
      ).flat()
    case 'LabeledGridTableConfig':
      return config.rows.flatMap((row) =>
        config.columns.map((column) => `${config.userInputKey}.${row.key}.${column.key}`)
      )
    case 'TextConfig':
    case 'FilesInputConfig':
    case 'ChecklistConfig':
    case undefined:
      return []
    default:
      return []
  }
}
